import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtPrice } from "@/lib/format";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MARKUP_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];
const emptyInventoryLine = () => ({
  line_type: "item",
  is_manual: false,
  item_id: "",
  name: "",
  specification: "",
  unit: "",
  qty: 1,
  cost_price: 0,
  markup_pct: 0,
  unit_price: 0,
  line_discount_pct: 0,
  line_gross: 0,
  line_discount: 0,
  line_total: 0,
});
const emptyManualLine = () => ({
  ...emptyInventoryLine(),
  line_type: "charge",
  is_manual: true,
  unit: "unit",
});

const CURRENCIES = ["USD", "IDR", "EUR", "AUD", "SGD"];

/**
 * Manual "New Invoice" creation dialog — the invoices page previously only
 * created invoices by converting from a DO/Order. This dialog lets a user
 * type a client + lines directly (mirrors the Quotations manual-entry flow).
 *
 * Behaviour:
 *  • Two "Add line" buttons: "+ From inventory" and "+ Manual entry".
 *  • Inventory lines use cost + markup → sell price like quotations.
 *  • Manual lines carry an optional cost_price so the Project Profitability
 *    report can compute margin without needing a linked quotation.
 *  • POSTs to /api/invoices with source="manual".
 */
export function NewInvoiceDialog({ open, onOpenChange, onCreated }) {
  const qc = useQueryClient();
  const { data: clients = [] } = useQuery({
    queryKey: ["clients-lite"],
    queryFn: () => api.listClients(),
    enabled: open,
  });
  const { data: items = [] } = useQuery({
    queryKey: ["items-lite"],
    queryFn: () => api.listItems(),
    enabled: open,
  });

  const [form, setForm] = useState(() => ({
    client_id: "",
    currency: "USD",
    tax_rate: 0,
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    notes: "",
    payment_method: "",
    items: [emptyInventoryLine()],
  }));

  const reset = () => setForm({
    client_id: "",
    currency: "USD",
    tax_rate: 0,
    issue_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    notes: "",
    payment_method: "",
    items: [emptyInventoryLine()],
  });

  // Recompute line-level derived numbers whenever the caller edits a field.
  const setLine = (idx, patch) => {
    setForm((f) => {
      const next = [...f.items];
      const cur = { ...next[idx], ...patch };
      const cost = Number(cur.cost_price || 0);
      const markup = Number(cur.markup_pct || 0);
      // If the caller didn't override unit_price, derive it from cost × markup.
      if (!("unit_price" in patch)) {
        cur.unit_price = +(cost * (1 + markup / 100)).toFixed(2);
      }
      const qty = Number(cur.qty || 0);
      const gross = +(qty * Number(cur.unit_price || 0)).toFixed(2);
      const discPct = Math.max(0, Math.min(100, Number(cur.line_discount_pct || 0)));
      const disc = +(gross * discPct / 100).toFixed(2);
      cur.line_gross = gross;
      cur.line_discount = disc;
      cur.line_total = +Math.max(0, gross - disc).toFixed(2);
      next[idx] = cur;
      return { ...f, items: next };
    });
  };

  // Selecting an item from the catalog snapshots cost / name / spec.
  const pickItem = (idx, itemId) => {
    const item = items.find((it) => it.id === itemId);
    if (!item) return;
    setLine(idx, {
      item_id: itemId,
      name: item.name,
      specification: item.specification || "",
      unit: item.unit || "",
      cost_price: Number(item.price || 0),
    });
  };

  const subtotal = useMemo(
    () => form.items.reduce((s, l) => s + Number(l.line_total || 0), 0),
    [form.items],
  );
  const tax = useMemo(
    () => +(subtotal * Number(form.tax_rate || 0) / 100).toFixed(2),
    [subtotal, form.tax_rate],
  );
  const total = +(subtotal + tax).toFixed(2);

  const createMut = useMutation({
    mutationFn: (body) => api.createInvoice(body),
    onSuccess: (inv) => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      toast.success(`Invoice ${inv.invoice_number} created`);
      reset();
      onOpenChange(false);
      onCreated?.(inv);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create invoice"),
  });

  const canSubmit =
    form.client_id &&
    form.items.some(
      (l) => Number(l.qty) > 0 && (l.item_id || (l.is_manual && (l.name || "").trim())),
    ) &&
    !createMut.isPending;

  const submit = () => {
    if (!canSubmit) return;
    createMut.mutate({
      client_id: form.client_id,
      currency: form.currency,
      tax_rate: Number(form.tax_rate || 0),
      issue_date: form.issue_date || undefined,
      due_date: form.due_date || undefined,
      payment_method: form.payment_method || undefined,
      notes: form.notes || "",
      items: form.items
        .filter((l) => Number(l.qty) > 0 && (l.item_id || (l.is_manual && (l.name || "").trim())))
        .map((l) => ({
          line_type: l.is_manual ? "charge" : "item",
          qty: Number(l.qty),
          unit_price: Number(l.unit_price || 0),
          cost_price: Number(l.cost_price || 0),
          line_discount_pct: Number(l.line_discount_pct || 0),
          ...(l.is_manual
            ? {
                name: (l.name || "").trim(),
                specification: l.specification || "",
                unit: l.unit || "unit",
              }
            : { item_id: l.item_id }),
        })),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent
        data-testid="new-invoice-dialog"
        className="sm:max-w-none max-w-none w-[min(96vw,1400px)] max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>New invoice</DialogTitle>
          <DialogDescription>
            Pick a client, add lines from inventory or type them manually,
            then save. The invoice will be created with{" "}
            <span className="font-medium">source = manual</span>.
          </DialogDescription>
        </DialogHeader>

        {/* Header fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="overline">Client</Label>
            <Select value={form.client_id}
              onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
              <SelectTrigger data-testid="new-invoice-client"><SelectValue placeholder="Select client…" /></SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Currency</Label>
            <Select value={form.currency}
              onValueChange={(v) => setForm((f) => ({ ...f, currency: v }))}>
              <SelectTrigger data-testid="new-invoice-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">Issue date</Label>
            <Input data-testid="new-invoice-issue-date"
              type="date" value={form.issue_date}
              onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))}
            />
          </div>
          <div>
            <Label className="overline">Due date</Label>
            <Input data-testid="new-invoice-due-date"
              type="date" value={form.due_date}
              onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
            />
          </div>
          <div>
            <Label className="overline">Tax %</Label>
            <Input data-testid="new-invoice-tax-rate"
              type="number" min="0" step="0.5"
              value={form.tax_rate}
              onChange={(e) => setForm((f) => ({ ...f, tax_rate: e.target.value }))}
              className="mono"
            />
          </div>
        </div>

        {/* Line items */}
        <div className="mt-3 border border-border rounded-md">
          <div className="px-3 py-2 border-b border-border flex justify-between items-center gap-2">
            <p className="overline">Line items</p>
            <div className="flex items-center gap-2">
              <Button type="button" data-testid="new-invoice-add-line" size="sm" variant="outline"
                onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyInventoryLine()] }))}>
                <Plus className="size-3.5 mr-1" /> From inventory
              </Button>
              <Button type="button" data-testid="new-invoice-add-manual-line" size="sm" variant="outline"
                className="border-primary/50 text-primary hover:bg-primary/5"
                onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyManualLine()] }))}>
                <Plus className="size-3.5 mr-1" /> Manual entry
              </Button>
            </div>
          </div>

          <div className="p-3 space-y-2">
            <div className="grid gap-2 text-[10px] uppercase text-muted-foreground font-semibold"
                 style={{ gridTemplateColumns: "minmax(160px,3fr) minmax(160px,3fr) 70px 110px 90px 110px 120px 70px" }}>
              <div>Item</div>
              <div>Description</div>
              <div className="text-right">Qty</div>
              <div className="text-right">Cost</div>
              <div className="text-right">Markup</div>
              <div className="text-right">Sell</div>
              <div className="text-right">Total</div>
              <div></div>
            </div>

            {form.items.map((l, idx) => l.is_manual ? (
              <div key={idx} data-testid={`new-invoice-line-${idx}`}
                   className="grid gap-2 items-start bg-amber-50/40 -mx-2 px-2 py-2 rounded"
                   style={{ gridTemplateColumns: "minmax(160px,3fr) minmax(160px,3fr) 70px 110px 90px 110px 120px 70px" }}>
                <div className="min-w-0">
                  <Input
                    data-testid={`new-invoice-line-name-${idx}`}
                    placeholder="Item name (paste or type)"
                    value={l.name}
                    onChange={(e) => setLine(idx, { name: e.target.value })}
                  />
                </div>
                <div className="min-w-0">
                  <Textarea
                    data-testid={`new-invoice-line-spec-${idx}`}
                    placeholder="Description (optional — wraps)"
                    value={l.specification}
                    onChange={(e) => setLine(idx, { specification: e.target.value })}
                    rows={1}
                    className="text-xs whitespace-pre-wrap break-words text-justify min-h-10 py-1.5"
                  />
                </div>
                <div className="min-w-0">
                  <Input data-testid={`new-invoice-line-qty-${idx}`} type="number" min="0" step="1"
                    value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })}
                    className="mono text-right" title={String(l.qty)} />
                </div>
                <div className="min-w-0">
                  <Input data-testid={`new-invoice-line-cost-${idx}`} type="number" min="0" step="0.01"
                    value={l.cost_price}
                    onChange={(e) => setLine(idx, { cost_price: e.target.value })}
                    className="mono text-right"
                    placeholder="Cost" title={String(l.cost_price)} />
                </div>
                <div className="min-w-0">
                  <Select value={String(Number(l.markup_pct || 0))}
                    onValueChange={(v) => setLine(idx, { markup_pct: Number(v) })}>
                    <SelectTrigger data-testid={`new-invoice-line-markup-${idx}`} className="mono px-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MARKUP_OPTIONS.map((m) => (
                        <SelectItem key={m} value={String(m)}>+{m}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-right mono text-xs text-muted-foreground pt-2 min-w-0 truncate"
                     data-testid={`new-invoice-line-sell-${idx}`}
                     title={fmtPrice(l.unit_price, form.currency)}>
                  {fmtPrice(l.unit_price, form.currency)}
                </div>
                <div className="text-right mono text-sm pt-2 font-medium min-w-0 truncate"
                     data-testid={`new-invoice-line-total-${idx}`}
                     title={fmtPrice(l.line_total, form.currency)}>
                  {fmtPrice(l.line_total, form.currency)}
                </div>
                <div className="flex items-center justify-end pt-1">
                  <Button type="button" variant="ghost" size="sm" className="text-red-600 h-8 w-8 p-0"
                    onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}>
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div key={idx} data-testid={`new-invoice-line-${idx}`}
                   className="grid gap-2 items-center"
                   style={{ gridTemplateColumns: "minmax(160px,3fr) minmax(160px,3fr) 70px 110px 90px 110px 120px 70px" }}>
                <div className="min-w-0">
                  <Select value={l.item_id} onValueChange={(v) => pickItem(idx, v)}>
                    <SelectTrigger data-testid={`new-invoice-line-item-${idx}`}>
                      <SelectValue placeholder="Select item…" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-muted-foreground truncate min-w-0" title={l.specification}>
                  {l.specification || "—"}
                </div>
                <div className="min-w-0">
                  <Input data-testid={`new-invoice-line-qty-${idx}`} type="number" min="0" step="1"
                    value={l.qty} onChange={(e) => setLine(idx, { qty: e.target.value })}
                    className="mono text-right" title={String(l.qty)} />
                </div>
                <div className="min-w-0">
                  <Input data-testid={`new-invoice-line-cost-${idx}`} type="number" min="0" step="0.01"
                    value={l.cost_price}
                    onChange={(e) => setLine(idx, { cost_price: e.target.value })}
                    className="mono text-right" title={String(l.cost_price)} />
                </div>
                <div className="min-w-0">
                  <Select value={String(Number(l.markup_pct || 0))}
                    onValueChange={(v) => setLine(idx, { markup_pct: Number(v) })}>
                    <SelectTrigger data-testid={`new-invoice-line-markup-${idx}`} className="mono px-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MARKUP_OPTIONS.map((m) => (
                        <SelectItem key={m} value={String(m)}>+{m}%</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-right mono text-xs text-muted-foreground min-w-0 truncate"
                     data-testid={`new-invoice-line-sell-${idx}`}
                     title={fmtPrice(l.unit_price, form.currency)}>
                  {fmtPrice(l.unit_price, form.currency)}
                </div>
                <div className="text-right mono text-sm min-w-0 truncate"
                     data-testid={`new-invoice-line-total-${idx}`}
                     title={fmtPrice(l.line_total, form.currency)}>
                  {fmtPrice(l.line_total, form.currency)}
                </div>
                <div className="flex items-center justify-end">
                  <Button type="button" variant="ghost" size="sm" className="text-red-600 h-8 w-8 p-0"
                    onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}>
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-border p-3 text-right text-sm mono space-y-1">
            <p>Subtotal: <span data-testid="new-invoice-subtotal">{fmtPrice(subtotal, form.currency)}</span></p>
            <p>Tax: <span data-testid="new-invoice-tax">{fmtPrice(tax, form.currency)}</span></p>
            <p className="font-bold text-primary text-base">
              Total: <span data-testid="new-invoice-total">{fmtPrice(total, form.currency)}</span>
            </p>
          </div>
        </div>

        <div className="mt-3">
          <Label className="overline">Notes (optional)</Label>
          <Textarea
            data-testid="new-invoice-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            data-testid="new-invoice-save"
            className="bg-primary hover:bg-primary/90"
            disabled={!canSubmit}
            onClick={submit}
          >
            Create invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
